import type { ReactNode } from "react";
import { IconMinus, IconPlus } from "./icons";

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

export function StepSection({
  step,
  title,
  sub,
  aside,
  children,
}: {
  step: number;
  title: string;
  sub?: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="px-5 py-4">
      <header className="mb-3 flex items-center gap-2.5">
        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-100 text-[12.5px] font-bold text-brand-700">
          {step}
        </span>
        <h2 className="text-[15px] font-bold text-slate-800">{title}</h2>
        {sub && <span className="text-[12px] text-slate-400">{sub}</span>}
        {aside && <div className="ml-auto">{aside}</div>}
      </header>
      {children}
    </section>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  title,
}: {
  value: T;
  options: { id: T; label: string; hint?: string }[];
  onChange: (v: T) => void;
  title?: string;
}) {
  return (
    <div>
      {title && <div className="mb-1.5 text-[12.5px] font-semibold text-slate-500">{title}</div>}
      <div className="seg">
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            title={o.hint}
            onClick={() => onChange(o.id)}
            className={cx("seg-item", value === o.id && "seg-item-on")}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Stepper({
  value,
  onChange,
  min = 1,
  max = 60,
  quick = [],
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  quick?: number[];
}) {
  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center rounded-xl border border-slate-200 bg-white">
        <button
          type="button"
          className="grid h-9 w-9 place-items-center rounded-l-xl text-slate-500 transition hover:bg-slate-50 disabled:opacity-30"
          onClick={() => onChange(clamp(value - 1))}
          disabled={value <= min}
          aria-label="減らす"
        >
          <IconMinus />
        </button>
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(clamp(Number(e.target.value) || min))}
          className="w-14 border-x border-slate-200 py-1.5 text-center text-[15px] font-bold text-slate-800 outline-none"
        />
        <button
          type="button"
          className="grid h-9 w-9 place-items-center rounded-r-xl text-slate-500 transition hover:bg-slate-50 disabled:opacity-30"
          onClick={() => onChange(clamp(value + 1))}
          disabled={value >= max}
          aria-label="増やす"
        >
          <IconPlus />
        </button>
      </div>
      {quick.map((q) => (
        <button
          key={q}
          type="button"
          onClick={() => onChange(clamp(q))}
          className={cx("chip", value === q && "border-brand-400 bg-brand-50 text-brand-700")}
        >
          {q}
        </button>
      ))}
    </div>
  );
}

export function Toast({ toasts, onDismiss }: { toasts: { id: number; text: string; tone: "ok" | "error" }[]; onDismiss: (id: number) => void }) {
  return (
    <div className="pointer-events-none fixed bottom-5 left-1/2 z-[60] flex -translate-x-1/2 flex-col items-center gap-2">
      {toasts.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onDismiss(t.id)}
          className={cx(
            "pop-in pointer-events-auto max-w-[min(90vw,560px)] rounded-xl px-4 py-2.5 text-[14px] font-medium text-white shadow-lg",
            t.tone === "ok" ? "bg-slate-800" : "bg-rose-600",
          )}
        >
          {t.text}
        </button>
      ))}
    </div>
  );
}

export function Spinner({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={cx(className, "animate-spin")} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity=".2" />
      <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
